import MaximumTransactionAmountSpendRule from './MaximumTransactionAmountSpendRule';
import ResponsiveButtonContainer from 'app/shared-components/layout/ResponsiveButtonContainer';
import MonthlyLimitSpendRule from './MonthlyLimitSpendRule';
import SpendingByCategory from 'app/pages/cards/spend-rules/CategorySpendRule';
import SpendingByCountry from 'app/pages/cards/spend-rules/CountrySpendRule';
import ProgressButton from 'app/shared-components/ui/ProgressButton';
import * as yup from 'yup';
import { ListType } from 'app/pages/cards/spend-rules/utils';
import { formatSentenceCase } from 'util/sanitizeText';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChecklistRtl } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { MerchantSpendRule } from 'API';
import type { GetCardsInput, GetCardsPayload } from 'CDK_API';
import { Box, Button, InputAdornment, SxProps, TextField, Theme, Typography } from '@mui/material';
import WarningModal from 'app/shared-components/ModalActionsCard/WarningModal';
import { SelectMultipleCards } from 'app/shared-components/select-multiple-cards';
import {
  fetchAssignableCardsLite,
  fetchCardsByPaymentCardIds,
} from 'app/pages/store/CardDataSlice/cardsThunks';
import {
  groupCardsSelector,
  resetAssinableCards,
  resetGroupCards,
} from 'app/pages/store/CardDataSlice/cardDataSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { debounce } from 'lodash';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';

const schema = yup.object({
  name: yup.string().required('You must enter a spend rule name'),
  spendingByCountry: yup.array().of(yup.string()).optional(),
  spendingByCategory: yup.array().of(yup.string()).optional(),
  spendingByCountryType: yup.mixed<ListType>().oneOf(Object.values(ListType)).optional(),
  spendingByCategoryType: yup.mixed<ListType>().oneOf(Object.values(ListType)).optional(),
  // SEE-1401: an empty field means "no limit"; an explicit 0 is never valid because a
  // $0 limit tells the card network to decline every transaction.
  maximumTransactionAmount: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .positive('Amount must be greater than 0')
    .optional(),
  monthlySpendLimit: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .positive('Amount must be greater than 0')
    .optional(),
  cards: yup.array().of(yup.string().required()).optional(),
});

export type EditSpendRuleFormValues = yup.InferType<typeof schema>;

export interface EditSpendRuleFormProps {
  selectedSpendRule?: MerchantSpendRule | undefined;
  copyFromSpendRule?: MerchantSpendRule | undefined;
  loading?: boolean;
  onClose?: () => void;
  handleSkip?: () => void;
  onSubmitForm: (values: EditSpendRuleFormValues) => void;
  sx?: SxProps<Theme>;
}

function EditSpendRuleForm({
  selectedSpendRule,
  copyFromSpendRule,
  loading,
  onClose,
  handleSkip,
  onSubmitForm,
  sx,
}: EditSpendRuleFormProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);
  const { assinableCards, assinableCardsLoading, groupCards, searchResults, searchResultsLoading } =
    useAppSelector(groupCardsSelector);
  const [isMonthlyLimitWarningOpen, setIsMonthlyLimitWarningOpen] = useState(false);
  const [isCardMonthlyLimitWarningOpen, setIsCardMonthlyLimitWarningOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<EditSpendRuleFormValues | null>(null);
  const [affectedCards, setAffectedCards] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');

  // Use ref to track the initial monthly spend limit value
  const initialMonthlySpendLimitRef = useRef<number | undefined>();
  // Use ref to track the initial list of cards when form opens
  const initialCardsRef = useRef<string[]>([]);

  // Calculate default values based on selectedSpendRule or copyFromSpendRule
  const defaultValues = useMemo(() => {
    // Use copyFromSpendRule if available (for copying), otherwise use selectedSpendRule (for editing)
    const sourceRule = copyFromSpendRule || selectedSpendRule;

    if (!sourceRule) {
      return {
        name: '',
        spendingByCountryType: ListType.NO_RESTRICTIONS,
        spendingByCategoryType: ListType.NO_RESTRICTIONS,
        spendingByCountry: [],
        spendingByCategory: [],
        monthlySpendLimit: undefined,
        maximumTransactionAmount: undefined,
        cards: [],
      };
    }

    const values = {
      name: sourceRule.name,
      spendingByCountryType: sourceRule.merchantCountryAllowed?.filter(Boolean).length
        ? ListType.ALLOW_LIST
        : sourceRule.merchantCountryBlocked?.filter(Boolean).length
        ? ListType.BLOCK_LIST
        : ListType.NO_RESTRICTIONS,
      spendingByCategoryType: sourceRule.merchantCategoryAllowed?.filter(Boolean).length
        ? ListType.ALLOW_LIST
        : sourceRule.merchantCategoryBlocked?.filter(Boolean).length
        ? ListType.BLOCK_LIST
        : ListType.NO_RESTRICTIONS,
      spendingByCountry:
        sourceRule.merchantCountryAllowed?.filter(Boolean) ||
        sourceRule.merchantCountryBlocked?.filter(Boolean) ||
        [],
      spendingByCategory:
        sourceRule.merchantCategoryAllowed?.filter(Boolean) ||
        sourceRule.merchantCategoryBlocked?.filter(Boolean) ||
        [],
      // SEE-1401: use a null check, not truthiness, so a legacy $0 row shows "0" (invalid,
      // must be fixed) instead of an empty "No limit" field that hides the enforced block.
      monthlySpendLimit:
        sourceRule.monthlyLimitAmount?.value != null ? sourceRule.monthlyLimitAmount.value / 100 : undefined,
      maximumTransactionAmount:
        sourceRule.maximumAmount?.value != null ? sourceRule.maximumAmount.value / 100 : undefined,
      cards: copyFromSpendRule
        ? []
        : (sourceRule.paymentCards || []).filter((card): card is string => card !== null),
    };

    return values;
  }, [selectedSpendRule, copyFromSpendRule]);

  // Create form methods with proper initialization
  const methods = useForm<EditSpendRuleFormValues>({
    mode: 'onChange',
    defaultValues,
    values: selectedSpendRule ? defaultValues : undefined, // Force initial values
    resolver: yupResolver(schema),
  });

  const { watch, setValue } = methods;
  const { control, getValues } = methods;
  const { errors, isValid, isDirty } = methods.formState;
  const buttonLabel = selectedSpendRule ? t('update') : t('save');

  // Set the initial monthly spend limit value when the form opens
  useEffect(() => {
    if (selectedSpendRule?.monthlyLimitAmount?.value) {
      initialMonthlySpendLimitRef.current = selectedSpendRule.monthlyLimitAmount.value / 100;
    } else {
      initialMonthlySpendLimitRef.current = undefined;
    }
    // Track initial cards list
    if (selectedSpendRule?.paymentCards) {
      initialCardsRef.current = (selectedSpendRule.paymentCards || []).filter(
        (card): card is string => card !== null
      );
    } else {
      initialCardsRef.current = [];
    }
  }, [selectedSpendRule]);

  const [
    spendingByCountry,
    spendingByCategory,
    maximumTransactionAmount,
    monthlySpendLimit,
    spendingByCountryType,
    spendingByCategoryType,
  ] = watch([
    'spendingByCountry',
    'spendingByCategory',
    'maximumTransactionAmount',
    'monthlySpendLimit',
    'spendingByCountryType',
    'spendingByCategoryType',
  ]);

  // Check if user is updating an existing monthly spend limit
  const isUpdatingMonthlyLimit = useMemo(() => {
    // Only show warning if:
    // 1. There's a selected spend rule (editing existing)
    // 2. There was an original monthly limit
    // 3. The monthly spend limit field has actually been modified from its initial value
    if (
      !selectedSpendRule ||
      initialMonthlySpendLimitRef.current === undefined ||
      monthlySpendLimit === undefined
    ) {
      return false;
    }

    // Check if the value is actually different from the initial value
    const hasChanged = Math.abs(monthlySpendLimit - initialMonthlySpendLimitRef.current) > 0.01;

    return hasChanged;
  }, [selectedSpendRule, monthlySpendLimit]);

  const missingCountryValues = useMemo(() => {
    return spendingByCountryType !== ListType.NO_RESTRICTIONS && spendingByCountry?.length === 0;
  }, [spendingByCountryType, spendingByCountry]);

  const missingCategoryValues = useMemo(() => {
    return spendingByCategoryType !== ListType.NO_RESTRICTIONS && spendingByCategory?.length === 0;
  }, [spendingByCategoryType, spendingByCategory]);

  const isMissing = (v: unknown) => v === null || v === undefined || v === '';
  const missingAllValues = useMemo(() => {
    const amountsMissing = isMissing(maximumTransactionAmount) && isMissing(monthlySpendLimit);

    if (
      spendingByCountryType === ListType.NO_RESTRICTIONS &&
      spendingByCategoryType === ListType.NO_RESTRICTIONS
    ) {
      return amountsMissing;
    }

    return missingCountryValues && missingCategoryValues && amountsMissing;
  }, [
    spendingByCountryType,
    spendingByCategoryType,
    maximumTransactionAmount,
    monthlySpendLimit,
    missingCountryValues,
    missingCategoryValues,
  ]);

  const handleSubmit = useCallback(
    (onSubmit: (values: EditSpendRuleFormValues) => void) => () => {
      const formValues = getValues();

      // Show warning if updating monthly spend limit
      if (isUpdatingMonthlyLimit) {
        setPendingFormValues(formValues);
        setIsMonthlyLimitWarningOpen(true);
        return;
      }

      // Check if cards with monthly limits are being added to a spend rule with monthly limit
      const currentCards: string[] = formValues.cards || [];
      const initialCards = initialCardsRef.current;
      const newCards = currentCards.filter((cardId: string) => !initialCards.includes(cardId));

      // Check if the spend rule has a monthly limit (only check form value to reflect post-save state)
      const hasMonthlyLimit =
        monthlySpendLimit !== undefined &&
        monthlySpendLimit !== null &&
        (typeof monthlySpendLimit === 'string' ? monthlySpendLimit !== '' : true);

      if (hasMonthlyLimit && newCards.length > 0) {
        // Find cards with monthly limits from the available card data
        const allCards = [...(isSearching ? searchResults : assinableCards), ...groupCards];
        const cardsWithMonthlyLimits = newCards
          .map((cardId: string) => allCards.find((card) => card.paymentCardId === cardId))
          .filter(
            (card): card is GetCardsPayload =>
              card !== undefined &&
              card?.monthlySpendLimit?.amount !== undefined &&
              card?.monthlySpendLimit?.amount !== null
          );

        if (cardsWithMonthlyLimits.length > 0) {
          // Get card identifiers (last4) for display
          const cardIdentifiers = cardsWithMonthlyLimits
            .map((card: GetCardsPayload) => `*${card?.last4 || 'XXXX'}`)
            .join(', ');
          setAffectedCards(cardIdentifiers.split(', '));
          setPendingFormValues(formValues);
          setIsCardMonthlyLimitWarningOpen(true);
          return;
        }
      }

      // Otherwise, submit directly
      onSubmit(formValues);
    },
    [
      getValues,
      isUpdatingMonthlyLimit,
      monthlySpendLimit,
      isSearching,
      searchResults,
      assinableCards,
      groupCards,
    ]
  );

  const handleContinueWithWarning = useCallback(() => {
    if (pendingFormValues) {
      onSubmitForm(pendingFormValues);
      setPendingFormValues(null);
    }
    setIsMonthlyLimitWarningOpen(false);
  }, [pendingFormValues, onSubmitForm]);

  const handleCloseWarning = useCallback(() => {
    setIsMonthlyLimitWarningOpen(false);
    setPendingFormValues(null);
  }, []);

  const handleContinueWithCardWarning = useCallback(() => {
    if (pendingFormValues) {
      onSubmitForm(pendingFormValues);
      setPendingFormValues(null);
    }
    setIsCardMonthlyLimitWarningOpen(false);
    setAffectedCards([]);
  }, [pendingFormValues, onSubmitForm]);

  const handleCloseCardWarning = useCallback(() => {
    setIsCardMonthlyLimitWarningOpen(false);
    setPendingFormValues(null);
    setAffectedCards([]);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedDispatch = useCallback(
    debounce((searchTerm: string) => {
      if (!selectedPaidolId) return; // Guard against undefined
      dispatch(
        fetchAssignableCardsLite({ paidolId: selectedPaidolId, searchTerm, excludeGroupedCards: false })
      );
    }, 1000),
    [dispatch, selectedPaidolId]
  );

  const loadCardList = useCallback(
    (searchTerm: string) => {
      // Trim whitespace and handle empty search
      const trimmedTerm = searchTerm.trim();
      setCurrentSearchTerm(trimmedTerm);

      // If search term is empty, load all cards immediately (no debounce)
      if (!trimmedTerm) {
        setIsSearching(false);
        if (selectedPaidolId) {
          dispatch(
            fetchAssignableCardsLite({
              paidolId: selectedPaidolId,
              searchTerm: '',
              excludeGroupedCards: false,
            })
          );
        }
        return;
      }

      // Search with trimmed term (debounced)
      setIsSearching(true);
      debouncedDispatch(trimmedTerm);
    },
    [debouncedDispatch, dispatch, selectedPaidolId]
  );

  useEffect(() => {
    return () => {
      dispatch(resetAssinableCards());
    };
  }, [dispatch]);

  // Reset groupCards when switching between spend rules
  useEffect(() => {
    dispatch(resetGroupCards());
  }, [selectedSpendRule?.id, dispatch]);

  // Fetch cards that are already attached to this spend rule
  useEffect(() => {
    if (selectedSpendRule?.paymentCards && selectedSpendRule.paymentCards.length > 0) {
      const input: GetCardsInput = {
        filter: {
          paidolId: { eq: selectedPaidolId },
          and: [
            {
              or: selectedSpendRule.paymentCards.map((cardId) => ({
                paymentCardId: { eq: cardId },
              })),
            },
          ],
        },
      };

      dispatch(fetchCardsByPaymentCardIds({ input }));
    }
  }, [selectedSpendRule, selectedPaidolId, dispatch]);

  useEffect(() => {
    return () => {
      // cancel pending debounced calls on unmount
      debouncedDispatch.cancel?.();
    };
  }, [debouncedDispatch]);

  const filteredCardOptions = useMemo(() => {
    const cards = isSearching ? searchResults : assinableCards;

    // Filter out cards that are attached to merchant spend rules
    // Cards with individual velocity limits (name pattern "Month (#XXXX)") should be included
    // Cards attached to merchant spend rules should be excluded
    const filteredByLimit = cards.filter((card) => {
      // If no monthlySpendLimit, include the card
      if (!card.monthlySpendLimit) return true;

      // Individual velocity limits have names like "Month (#2836)"
      // Merchant spend rules have custom names (like "Sanity Test")
      const limitName = card.monthlySpendLimit.name || '';
      const isIndividualLimit = /^Month \(#\d+\)$/.test(limitName);

      return isIndividualLimit;
    });

    // If searching, filter by cardholder name client-side if search term contains letters
    // Backend can't search cardholderName via wildcard (maps to cardholderId)
    // Backend searches by last4 if search term contains digits
    if (isSearching && currentSearchTerm && currentSearchTerm.match(/[a-zA-Z]/)) {
      const searchLower = currentSearchTerm.toLowerCase();
      return filteredByLimit.filter((card) => {
        const cardholderName = card.cardholder?.name || card.name || '';
        return cardholderName.toLowerCase().includes(searchLower);
      });
    }

    return filteredByLimit;
  }, [isSearching, searchResults, assinableCards, currentSearchTerm]);

  return (
    <FormProvider {...methods} key={selectedSpendRule?.id || copyFromSpendRule?.id || 'new'}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          ...sx,
        }}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="text"
              error={!!errors.name}
              helperText={errors?.name?.message}
              label={t('spendRules.ruleName')}
              id="name"
              variant="outlined"
              required
              onBlur={(e) => field.onChange(formatSentenceCase(e.target.value))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ChecklistRtl />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <SpendingByCountry control={control} selectedSpendRule={copyFromSpendRule || selectedSpendRule} />
        <SpendingByCategory control={control} selectedSpendRule={copyFromSpendRule || selectedSpendRule} />
        <MaximumTransactionAmountSpendRule selectedSpendRule={copyFromSpendRule || selectedSpendRule} />
        <MonthlyLimitSpendRule selectedSpendRule={copyFromSpendRule || selectedSpendRule} />

        {/* Card Selection Component */}
        <Controller
          name="cards"
          control={control}
          render={({ field }) => (
            <SelectMultipleCards
              field={field}
              options={filteredCardOptions}
              selectedOptions={groupCards}
              loading={(isSearching ? searchResultsLoading : assinableCardsLoading) || loading}
              onChange={(cards) => {
                setValue('cards', cards, {
                  shouldDirty: true,
                  shouldValidate: true,
                  shouldTouch: true,
                });
              }}
              onInput={loadCardList}
              onOpen={() => loadCardList('')}
            />
          )}
        />
      </Box>

      <ResponsiveButtonContainer>
        {onClose && <Button onClick={onClose}>{t('cancel')}</Button>}
        {handleSkip && (
          <Button onClick={handleSkip} variant="outlined" size="small">
            {t('illDoThisLater')}
          </Button>
        )}
        <ProgressButton
          size={onClose ? 'medium' : 'small'}
          loading={loading}
          onClick={handleSubmit(onSubmitForm)}
          disabled={
            !isValid ||
            !isDirty ||
            loading ||
            missingCountryValues ||
            missingCategoryValues ||
            missingAllValues
          }
        >
          {buttonLabel}
        </ProgressButton>
      </ResponsiveButtonContainer>

      {/* Warning Modal for Monthly Spend Limit Updates */}
      <WarningModal
        isLoading={!!loading}
        isOpen={isMonthlyLimitWarningOpen}
        onClose={handleCloseWarning}
        onContinue={handleContinueWithWarning}
        modalHeader={t('spendRules.monthlyLimitUpdateWarning.header')}
        continueButtonText={t('spendRules.monthlyLimitUpdateWarning.continue')}
      >
        <Typography variant="body1" color="text.primary">
          {t('spendRules.monthlyLimitUpdateWarning.message', {
            newLimit: (() => {
              const actualMonthlyLimit = pendingFormValues?.monthlySpendLimit;
              if (typeof actualMonthlyLimit === 'string') {
                // For string inputs, preserve the original precision
                return actualMonthlyLimit;
              } else if (typeof actualMonthlyLimit === 'number') {
                return actualMonthlyLimit.toString();
              }
              return '0.00';
            })(),
          })}
        </Typography>
      </WarningModal>

      {/* Warning Modal for Cards with Monthly Limits Being Added */}
      <WarningModal
        isLoading={!!loading}
        isOpen={isCardMonthlyLimitWarningOpen}
        onClose={handleCloseCardWarning}
        onContinue={handleContinueWithCardWarning}
        modalHeader={t('spendRules.cardMonthlyLimitResetWarning.header')}
        continueButtonText={t('spendRules.cardMonthlyLimitResetWarning.continue')}
      >
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          {t('spendRules.cardMonthlyLimitResetWarning.message')}
        </Typography>
        {affectedCards.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('spendRules.cardMonthlyLimitResetWarning.affectedCards', {
              cards: affectedCards.join(', '),
            })}
          </Typography>
        )}
      </WarningModal>
    </FormProvider>
  );
}

export default EditSpendRuleForm;
